import { Clock, Effect, Option, Result, Schema as S } from "effect";
import * as AsyncData from "foldkit/asyncData";
import * as Command from "foldkit/command";
import * as Navigation from "foldkit/navigation";
import { evo } from "foldkit/struct";

import { settle, start } from "../../api/asyncData";
import {
	createNote,
	getNote,
	listFolders,
	saveNote,
} from "../../api/commands/notes";
import { getRecord } from "../../api/commands/records";
import { type ApiError, toMessage } from "../../api/errors";
import type { NoteDetail } from "../../api/schemas/notes";
import { Route, linkHref } from "../../app/routes";
import * as NoteEditor from "../../widgets/noteEditor";
import { safeHref } from "../../widgets/wikiLinks";
import type { Page, PageReturn, Shared } from "../shared";
import { Message } from "./message";
import type { Draft, Model } from "./model";

/**
 * Note: one knowledge-base note, its wiki links, backlinks and editor.
 *
 * U15: the note is always open in one live-preview editor (no separate read
 * and preview modes). `draft` is Some only while title or body differ from
 * the saved note, so Save and Discard show exactly when there is something
 * to save. Mod-click / Mod-Enter on a `[[link]]` in the editor opens it:
 * resolved navigates, ambiguous opens the chooser, broken creates the note.
 *
 * KTD8: notes are not time-versioned, so every fetch is live and tagged by
 * generation (and note id) only. While `Shared.asOf` is set the view shows a
 * one-line "live data" notice, the editor is read-only, and every write is
 * blocked: edits, save, and "create note" on broken links.
 *
 * Saves send the version the note was read at as `baseVersion`; a 409 means
 * someone saved first, so the page keeps the draft and offers a reload.
 * After the note arrives, the records behind person links (mini-briefs) and
 * ambiguous candidates (chooser labels) are fetched one by one; a failed
 * record fetch only costs that label.
 */

/** The editor's registry id: stable per note so re-renders never remount. */
export const editorId = (id: string) => `note-${id}`;

export const FetchNote = Command.define("FetchNote", {
	args: { id: S.String, generation: S.Number },
	messages: [Message.CompletedFetchNote],
	execute: ({ id, generation }) =>
		Effect.all([Clock.currentTimeMillis, getNote.run({ id })]).pipe(
			Effect.map(([now, result]) =>
				Message.CompletedFetchNote({ id, generation, now, result }),
			),
		),
});
export const FetchFolders = listFolders.command(
	Message.CompletedFetchFolders,
	({ generation, result }) =>
		Message.CompletedFetchFolders({ generation, result }),
);
export const FetchRecord = getRecord.command(
	Message.CompletedFetchRecord,
	({ nodeId, noteId, generation, result }) =>
		Message.CompletedFetchRecord({
			noteId,
			recordId: nodeId,
			generation,
			result,
		}),
	{ noteId: S.String },
);
export const CreateNote = createNote.command(
	Message.CompletedCreate,
	({ generation, result }) => Message.CompletedCreate({ generation, result }),
);
export const SaveNote = saveNote.command(
	Message.CompletedSave,
	({ id, generation, result }) =>
		Message.CompletedSave({ id, generation, result }),
);
export const Navigate = Command.define("NavigateFromNote", {
	args: { url: S.String },
	messages: [Message.CompletedNavigate],
	execute: ({ url }) =>
		Navigation.pushUrl(url).pipe(Effect.as(Message.CompletedNavigate())),
});

export const blank = (id: string): Model => ({
	id,
	note: AsyncData.Idle(),
	folders: AsyncData.Idle(),
	toggled: [],
	now: 0,
	records: {},
	chooser: Option.none(),
	draft: Option.none(),
	saving: false,
	conflict: false,
	creating: Option.none(),
	error: Option.none(),
});

type Return = PageReturn<Model, Message>;

const load = (id: string, shared: Shared): Return => ({
	model: evo(blank(id), { note: start, folders: start }),
	commands: [
		FetchNote({ id, generation: shared.generation }),
		FetchFolders({ generation: shared.generation }),
	],
});

const routeId = (route: Route) => (route._tag === "Note" ? route.id : "");

/** Ids worth a record fetch: person links (mini-brief) and ambiguous candidates (chooser labels). */
export const recordIds = (detail: NoteDetail): ReadonlyArray<string> => [
	...new Set(
		detail.links.flatMap((link) =>
			link.status === "ambiguous"
				? link.ids
				: link.status === "resolved" && link.type === "person"
					? [link.id]
					: [],
		),
	),
];

/** Notes open in Knowledge; everything else a link can reach opens as a record. */
export const hrefFor = (shared: Shared, id: string, type: string | undefined) =>
	safeHref(
		linkHref(
			shared.route,
			type === "note"
				? Route.Note({ id, asOf: Option.none() })
				: Route.Record({ id, asOf: Option.none(), via: Option.none() }),
		),
	);

const savedNote = (model: Model) =>
	Option.getOrNull(AsyncData.getData(model.note))?.note;

/** Applies an edit; the draft drops back to None when it matches the saved note again. */
const edited = (model: Model, patch: Partial<Draft>): Model => {
	const note = savedNote(model);
	if (!note) return model;
	const next = {
		...Option.getOrElse(model.draft, () => ({
			title: note.title,
			body: note.body,
		})),
		...patch,
	};
	return evo(model, {
		draft: () =>
			next.title === note.title && next.body === note.body
				? Option.none()
				: Option.some(next),
	});
};

const createLinked = (model: Model, shared: Shared, target: string): Return =>
	Option.isSome(model.creating) || Option.isSome(shared.asOf)
		? { model }
		: {
				model: evo(model, {
					creating: () => Option.some(target),
					error: () => Option.none(),
				}),
				commands: [
					CreateNote({
						title: target,
						body: "",
						generation: shared.generation,
					}),
				],
			};

const save = (model: Model, shared: Shared): Return => {
	const draft = Option.getOrNull(model.draft);
	const note = savedNote(model);
	return !draft ||
		!note ||
		model.saving ||
		model.conflict ||
		Option.isSome(shared.asOf) ||
		draft.title.trim() === ""
		? { model }
		: {
				model: evo(model, {
					saving: () => true,
					error: () => Option.none(),
				}),
				commands: [
					SaveNote({
						id: model.id,
						baseVersion: note.version ?? "",
						title: draft.title.trim(),
						body: draft.body,
						generation: shared.generation,
					}),
				],
			};
};

const activate = (model: Model, shared: Shared, raw: string): Return => {
	const link = Option.getOrNull(AsyncData.getData(model.note))?.links.find(
		(l) => l.raw === raw,
	);
	if (!link) return { model };
	switch (link.status) {
		case "resolved": {
			const url = hrefFor(shared, link.id, link.type);
			return url ? { model, commands: [Navigate({ url })] } : { model };
		}
		case "ambiguous":
			return { model: evo(model, { chooser: () => Option.some(raw) }) };
		case "broken":
			return createLinked(model, shared, link.target);
	}
};

const isConflict = (error: ApiError) =>
	error._tag === "Http" && error.status === 409;

export const page: Page<Model, Message> = {
	init: (route, shared) => load(routeId(route), shared),
	// A new id is a different note: start over. A new asOf changes nothing (KTD8).
	changedRoute: (model, _previous, next, shared) =>
		routeId(next) === model.id ? { model } : load(routeId(next), shared),
	update: (model, message, shared) =>
		Message.match<Return>(message, {
			CompletedFetchNote: ({ id, generation, now, result }) => {
				if (id !== model.id || generation !== shared.generation)
					return { model };
				const next = evo(model, {
					note: (note) => settle(note, result),
					now: () => now,
				});
				return Result.match(result, {
					onFailure: () => ({ model: next }),
					onSuccess: (detail) => ({
						model: next,
						commands: recordIds(detail)
							.filter((rid) => !(rid in model.records))
							.map((nodeId) =>
								FetchRecord({
									nodeId,
									asOf: null,
									noteId: id,
									generation,
								}),
							),
					}),
				});
			},
			CompletedFetchFolders: ({ generation, result }) =>
				generation !== shared.generation
					? { model }
					: {
							model: evo(model, {
								folders: (folders) => settle(folders, result),
							}),
						},
			ToggledFolder: ({ path }) => ({
				model: evo(model, {
					toggled: (toggled) =>
						toggled.includes(path)
							? toggled.filter((p) => p !== path)
							: [...toggled, path],
				}),
			}),
			CompletedFetchRecord: ({ noteId, recordId, generation, result }) =>
				noteId !== model.id ||
				generation !== shared.generation ||
				Result.isFailure(result)
					? { model }
					: {
							model: evo(model, {
								records: (records) => ({
									...records,
									[recordId]: result.success,
								}),
							}),
						},
			ToggledChooser: ({ raw }) => ({
				model: evo(model, {
					chooser: (open) =>
						raw === null || Option.getOrNull(open) === raw
							? Option.none()
							: Option.some(raw),
				}),
			}),
			ClickedCreateLinked: ({ target }) => createLinked(model, shared, target),
			CompletedCreate: ({ generation, result }) =>
				generation !== shared.generation
					? { model }
					: Result.match(result, {
							onSuccess: ({ note }) => ({
								model: evo(model, { creating: () => Option.none() }),
								commands: [
									Navigate({
										url: linkHref(
											shared.route,
											Route.Note({ id: note.id, asOf: Option.none() }),
										),
									}),
								],
							}),
							onFailure: (error) => ({
								model: evo(model, {
									creating: () => Option.none(),
									error: () => Option.some(toMessage(error)),
								}),
							}),
						}),
			CompletedNavigate: () => ({ model }),
			ClickedCancel: () => {
				const note = savedNote(model);
				return {
					model: evo(model, {
						draft: () => Option.none(),
						conflict: () => false,
						error: () => Option.none(),
					}),
					commands:
						note && Option.isSome(model.draft)
							? [
									NoteEditor.SetNoteText({
										id: editorId(model.id),
										text: note.body,
									}),
								]
							: [],
				};
			},
			ChangedTitle: ({ value }) => ({
				model: Option.isSome(shared.asOf)
					? model
					: edited(model, { title: value }),
			}),
			GotNoteEditorMessage: ({ message: editorMessage }) =>
				editorMessage.id !== editorId(model.id)
					? { model }
					: NoteEditor.Message.match<Return>(editorMessage, {
							NoteEditorChanged: ({ text }) => ({
								model: Option.isSome(shared.asOf)
									? model
									: edited(model, { body: text }),
							}),
							NoteEditorActivatedLink: ({ raw }) =>
								activate(model, shared, raw),
							NoteEditorRequestedSave: () => save(model, shared),
						}),
			CompletedNoteEditorCommand: () => ({ model }),
			ClickedSave: () => save(model, shared),
			CompletedSave: ({ id, generation, result }) =>
				id !== model.id || generation !== shared.generation
					? { model }
					: Result.match(result, {
							onSuccess: (saved) => ({
								commands: [
									NoteEditor.SetNoteLinks({
										id: editorId(model.id),
										links: NoteEditor.editorLinks(saved.links),
									}),
								],
								model: evo(model, {
									saving: () => false,
									draft: () => Option.none(),
									note: (note) =>
										AsyncData.map(note, (detail) => ({
											...detail,
											...saved,
										})),
								}),
							}),
							onFailure: (error) => ({
								model: evo(model, {
									saving: () => false,
									conflict: () => isConflict(error),
									error: () =>
										isConflict(error)
											? Option.none()
											: Option.some(toMessage(error)),
								}),
							}),
						}),
			// Drops the draft: the reader asked for the other version.
			ClickedReload: () => load(model.id, shared),
		}),
};
